/**
 * 中文翻译
 */
import type { TranslationKeys } from './en';

type StringShape<T> = {
  [K in keyof T]: T[K] extends string ? string : StringShape<T[K]>;
};

const zh: StringShape<TranslationKeys> = {
  // ── App shell ──
  app: {
    name: 'Boox Platform',
    logo: 'B',
  },
  tabs: {
    closeAll: '关闭全部标签',
    all: '全部',
  },
  commonAction: {
    refresh: '刷新',
    runNow: '立即执行',
    newTask: '新建任务',
    newLocalService: '新建本地服务',
    create: '创建',
    save: '保存',
  },
  shippingAgency: {
    accounts: {
      pageTitle: '船务代理账户',
      activeAccounts: '活跃账户',
      creditHold: '信用冻结',
      chargeProfiles: '本地收费配置',
    },
    principalTasks: {
      queueTitle: 'Principal 任务台',
      queued: '待执行',
      running: '执行中',
      needsOverride: '待人工接管',
      empty: '暂无 principal 集成任务。',
      runReady: '已生成可执行的认证浏览器计划',
    },
    localServices: {
      pageTitle: '本地服务工作台',
      truckingOrders: '拖车任务',
      depotOrders: '堆场任务',
      backlog: '待处理服务积压',
      revenuePlanned: '计划收入',
      marginPlanned: '计划毛利',
      empty: '暂无本地服务单。',
      filtered: '已按订舱 {{bookingNo}} 过滤',
      createTitle: '创建本地服务单',
      loadingQueue: '正在加载本地服务协同队列…',
      vendor: '供应商',
      container: '箱号',
      scheduled: '计划时间',
      revenue: '收入',
      cost: '成本',
      margin: '毛利',
      customerBilling: '客户结算',
      vendorCost: '供应商结算',
      openArDraft: '打开应收草稿',
      openApDraft: '打开应付草稿',
      openServiceDesk: '打开服务工单台',
      openDepotMoves: '打开堆场调度台',
      generateArDraft: '生成应收草稿',
      generateApDraft: '生成应付草稿',
      arDraftReady: '应收草稿已就绪',
      apDraftReady: '应付草稿已就绪',
      postCustomerInvoice: '过账客户账单',
      postVendorBill: '过账供应商账单',
      confirmReceipt: '确认收款',
      confirmVendorPayment: '确认付款',
      customerInvoicePosted: '客户账单已过账',
      vendorBillPosted: '供应商账单已过账',
      customerReceiptConfirmed: '客户收款已确认',
      vendorPaymentConfirmed: '供应商付款已确认',
      settlementActionFailed: '结算动作执行失败',
      notStarted: '未开始',
      draftReady: '草稿已就绪',
      posted: '已过账',
      partiallySettled: '部分结清',
      settled: '已结清',
      voided: '已作废',
      pending: '待处理',
    },
    emailIntake: {
      pageTitle: 'AI 邮件 Intake',
      subtitle: '把代理邮件转成 booking、单证、放货、本地服务或 Evergreen 任务草稿。',
      from: '发件人',
      to: '收件人',
      subject: '主题',
      body: '邮件正文',
      autoCreate: '自动创建草稿工作项',
      submit: '处理邮件',
      result: '处理结果',
      draftKind: '草稿类型',
      linkedBooking: '关联订舱',
    },
  },
  depotModule: {
    dashboardTitle: 'Depot 运营总览',
    dashboardSubtitle: 'Gate、yard、EIR、inspection、move 和 billing 在一个台面。',
  },
  socModule: {
    dashboardTitle: 'SOC 箱管控制塔',
    dashboardSubtitle: '统一查看箱池可用量、放箱执行、逾期回箱和费用暴露。',
  },

  // ── Login ──
  login: {
    title: 'Boox Platform',
    subtitle: '登录以继续',
    email: '邮箱',
    loginName: '登录名',
    password: '密码',
    submit: '登录',
    submitting: '登录中…',
    failed: '登录失败',
    heroTitle: '智能企业运营系统',
    heroDesc: '自动化运营、跟踪预订、在一个平台中管理您的整个运营工作流程。',
    feature1: '实时追踪',
    feature2: '智能自动化',
    feature3: '多租户支持',
    welcomeBack: '欢迎回来',
    forgotPassword: '忘记密码？',
    securityNote: '您的连接已加密并安全。',
    back: '返回',
    selectTenant: '选择工作区',
    brandingLoading: '租户品牌信息仍在加载，请稍后再试。',
    secureAccessFor: '{{company}} 专属登录入口',
    workspaceOnHost: '{{portal}} · {{host}}',
    tenantWorkspace: '租户工作区',
    shortAccountHint: '这里支持短账号登录，例如 {{hint}}',
    dedicatedLogin: '这是 {{company}} 的专属登录入口。',
    unauthorizedTenant: '您无权访问此应用。',
    noTenantAccess: '您的账号没有可访问的租户。',
    invalidCredentials: '用户名或密码错误。',
    bridgeFailed: '继续登录失败',
  },

  // ── Dashboard ──
  dashboard: {
    title: '仪表盘',
    welcome: '欢迎回来，{{name}}',
    goodMorning: '早上好',
    goodAfternoon: '下午好',
    goodEvening: '晚上好',
    fields: '字段',
    children: '子实体',
    relations: '关联',
  },

  // ── Per-category workspace pages ──
  workspace: {
    overview:        '概览',
    subtitle:        '{{count}} 个实体',
    quickLinks:      '快捷入口',
    noEntities:      '此工作区无实体',
    noEntitiesDesc:  '请在管理面板配置此分类的实体。',
    notFound:        '未找到工作区',
    notFoundDesc:    '此工作区未配置或您没有访问权限。',
  },

  // ── Sidebar category labels ──
  category: {
    // ── Core ──
    home:         '首页',
    builder:      '业务构建器',
    inbox:        '收件箱',
    business:     '商务',
    // ── Verticals ──
    transport:    'Transport Hero V1',
    fleet:        '车队',
    freight:      '货运领航',
    warehouse:    '仓储流',
    haulage:      '旧版集卡 (HMS)',
    'supply-chain': '供应链导航',
    service:      '服务管理',
    sas:          '海事工作台',
    depot:        '堆场控制台',
    soc:          '自备箱',
    // ── Support ──
    finance:      '财务',
    audit:        '审计与合规',
    workforce:    '人力',
    // ── Admin (bottom-pinned) ──
    settings:     '设置',
    platform:     '平台',
    // ── Legacy (backward compat) ──
    tms:          'Transport Hero V1',
    fms:          'FMS',
    wms:          'WMS',
    accounting:   '账务',
    ai:           'AI',
    'ai-command': 'AI 指挥',
    trucking:     '货运',
    accounts:     '账务',
    parties:      '客户',
    messaging:    '消息',
    agent:        '智能',
    config:       '配置',
    master:       '主数据',
    transaction:  '业务',
    event:        '事件',
    attachment:   '附件',
    communication:'通讯',
    pro2:         'CMS PRO2',
    // ── 网络 ──
    network:      'Boox 网络',
    // ── CC（指挥中心）分类 ──
    tenants:      '租户管理',
    'ai-center':  'AI 中心',
    'cc-home':      '指挥中心',
    'cc-tenants':   '租户运营',
    'cc-brain':     'AI & Brain',
    'cc-builder':   '构建器',
    'cc-observe':   '可观测性',
    'cc-analytics': '分析中心',
    'cc-command': '指挥台',
    'cc-revenue': '营收运营',
    'cc-delivery': '交付运营',
    'cc-ceo': 'CEO',
    'cc-tenant-admin': '租户治理',
    'cc-systems': '平台系统',
  },

  // ── Sidebar group labels ──
  group: {
    // ── Home groups ──
    Dashboards:       '仪表盘',
    'Quick Access':   '快捷入口',
    // ── Inbox groups ──
    Messages:         '消息',
    Tasks:            '任务',
    // ── Business groups ──
    Sales:            '销售',
    Purchase:         '采购',
    // ── Transport groups ──
    Operations:       '操作',
    // ── Freight groups ──
    Oversea:          '海外',
    // ── Finance groups ──
    AR:               '应收账款',
    AP:               '应付账款',
    GL:               '总账',
    // ── Workforce groups ──
    People:           '人员',
    Branches:         '分支机构',
    Access:           '权限',
    // ── Settings groups ──
    Tenant:           '租户',
    System:           '系统',
    // ── Platform groups ──
    'AI Admin':       'AI 管理',
    DevOps:           '运维',
    // ── CC（指挥中心）分组 ──
    Tenants:          '租户',
    Channels:         '渠道',
    Security:         '安全',
    Rules:            '规则',
    'AI Config':      'AI 配置',
    Brain:            'Brain 大脑',
    Logs:             '日志',
    Dev:              '开发',
    Infra:            '基础设施',
    'Platform Intelligence': '平台智能',
    overview:         '总览',
    runtime:          '运行态',
    tools:            '工具',
    intake:           '入口',
    qualification:    '资格评估',
    governance:       '治理',
    delivery:         '交付',
    readiness:        '就绪度',
    executive:        '高层执行',
    configuration:    '配置',
    build:            '构建',
    'ai-runtime':     'AI 运行时',
    systems:          '系统',
    monitoring:       '监控',
    // ── Shared groups ──
    Setup:            '设置',
    Reports:          '报表',
    'Master Data':    '主数据',
    Config:           '配置',
    Monitor:          '监控',
    // ── Legacy group keys ──
    'Container Trucking': '集装箱卡车',
    'Local Transport':    '本地运输',
    Sea:              '海运',
    Air:              '空运',
    'Local Handling': '本地操作',
    Customers:        '客户',
    'Business Parties': '商业伙伴',
    Employees:        '员工',
    AI:               'AI',
    Driver:           '司机',
    Workflow:         '工作流',
    work:             '工作',
    setup:            '设置',
    transactions:     '交易',
    inventory:        '库存',
    reports:          '报表',
    tax:              '税务',
    customers:        '客户',
    partners:         '合作伙伴',
    team:             '团队',
    inbox:            '收件箱',
    email:            '邮件',
    channels:         '频道',
    pipeline:         '流水线',
    tasks:            '任务',
    files:        '文件',
    audit:        '审计',
    ui:           'UI模板',
    system:       '系统',
    // ── Entity category labels ──
    people:       '人员',
    jobs:         '工单',
    shipping:     '运输',
    finance:      '财务',
    warehouse:    '仓储',
    messaging:    '消息',
    'inventory-events': '库存事件',
    // ── Module nav group labels (lowercase, used by MODULE_NAV_ITEMS) ──
    dashboard:        '仪表盘',
    orders:           '订单',
    operations:       '操作',
    import:           '进口',
    export:           '出口',
    master:           '主数据',
    Import:           '进口',
    Export:           '出口',
    Master:           '主数据',
    overall:          '总览',
    operating:        '运营',
    category:         '分类',
    planning:         '计划',
    banking:          '银行',
    admin:            '管理',
    'self-service':   '自助服务',
    config:           '配置',
    tenants:          '租户',
    policies:         '策略',
    agents:           'Agents',
    knowledge:        '知识',
    schema:           '模型',
    templates:        '模板',
    automation:       '自动化',
    devtools:         '开发工具',
    integration:      '集成',
    deployment:       '部署',
    communication:    '沟通',
    breakdown:        '拆分',
    realtime:         '实时',
    trends:           '趋势',
    // ── Finance module groups (new) ──
    processes:        '流程',
    management:       '管理报表',
    einvoicing:       '电子发票',
    compliance:       '合规',
    // ── Network groups ──
    collaboration:    '协作',
    // ── Other new groups ──
    review:           '审核',
    crm:              '客户关系',
    messages:         '消息',
    schedule:         '排期',
    docs:             '文件',
    // ── Module sidebar groups (used by MODULE_NAV_ITEMS) ──
    ai:               'AI',
    'boox-network':   'Boox 网络',
    circles:          '圈子',
    contacts:         '联系人',
    fulfillment:      '履约',
    personal:         '个人',
    procurement:      '采购',
    requests:         '申请',
    resources:        '资源',
    safety:           '安全',
    sales:            '销售',
    sourcing:         '寻源',
    tenant:           '租户',
    views:            '视图',
    "collections": "收藏",
    "control": "控制",
    "core": "核心",
    "devops": "开发运营",
    "execution": "执行",
    "fleet": "舰队",
    "master-data": "主数据",
    "ops": "运营",
    "platform": "平台",
    "workshop": "车间",
    "workspace": "工作空间",
  },

  // ── Entity list ──
  list: {
    totalRecords: '共 {{count}} 条记录',
    newBtn: '新建',
    cancel: '取消',
    createTitle: '新建 {{label}}',
    createSuccess: '{{label}} 创建成功',
    search: '搜索 {{label}}…',
    allStatus: '全部状态',
    loading: '加载中…',
    noData: '无数据',
    page: '第 {{current}} / {{total}} 页',
    loadFailed: '数据加载失败',
    notFound: '实体未找到',
  },

  // ── Entity detail ──
  detail: {
    loadFailed: '加载详情失败',
    notFound: '记录未找到',
    confirmDeleteTitle: '确认删除',
    confirmDeleteDesc: '确定要删除此 {{label}} 记录吗？此操作不可撤销。',
    delete: '删除',
    deleteSuccess: '删除成功',
    deleteFailed: '删除失败',
    actionSuccess: '{{action}} 执行成功',
    actionFailed: '{{action}} 执行失败',
    cancelEdit: '取消编辑',
    edit: '编辑',
    editTitle: '编辑 {{label}}',
    tabHuman: '完整视图',
    tabBasic: '基本',
    tabJson: 'JSON',
  },

  // ── Entity edit form ──
  form: {
    required: '{{field}} 为必填项',
    minValue: '最小值为 {{min}}',
    maxValue: '最大值为 {{max}}',
    invalidFormat: '格式不正确',
    selectPlaceholder: '请选择…',
    boolTrue: '是',
    boolFalse: '否',
    saveSuccess: '保存成功',
    createSuccess: '创建成功',
    saveFailed: '保存失败',
    save: '保存',
    create: '创建',
    cancel: '取消',
  },

  // ── Human view ──
  humanView: {
    summary: '概要',
    relatedData: '关联数据',
    childEntities: '子实体',
    moreRows: '还有 {{count}} 条…',
    boolTrue: '是',
    boolFalse: '否',
    empty: '—',
  },

  // ── Basic view ──
  basicView: {
    basicInfo: '基本信息',
    extendedData: '扩展数据',
    boolTrue: '是',
    boolFalse: '否',
    empty: '—',
  },

  // ── View template fallback sections ──
  viewTemplate: {
    basicInfo: '基本信息',
    financialInfo: '财务信息',
    timeline: '时间线',
    otherInfo: '其他信息',
  },

  // ── Export ──
  export: {
    csv: '导出 CSV',
    success: '导出成功',
    failed: '导出失败，请重试',
  },

  // ── Notifications ──
  notification: {
    title: '通知',
    markAllRead: '全部已读',
    loading: '加载中…',
    empty: '暂无通知',
    justNow: '刚刚',
    minutesAgo: '{{count}} 分钟前',
    hoursAgo: '{{count}} 小时前',
    daysAgo: '{{count}} 天前',
  },

  // ── Confirm dialog ──
  confirm: {
    ok: '确认',
    cancel: '取消',
    processing: '处理中…',
  },

  // ── Error boundary ──
  error: {
    title: '出错了',
    fallback: '发生了意外错误。',
    tryAgain: '重试',
    goHome: '返回首页',
  },

  // ── Search / Command Palette ──
  search: {
    placeholder: '搜索实体… ⌘K',
    noResults: '未找到匹配实体',
    navigate: '导航',
    open: '打开',
    results: '{{count}} 个结果',
  },

  // ── User dropdown ──
  user: {
    profile: '个人资料',
    settings: '设置',
    shortcuts: '快捷键',
    signOut: '退出登录',
    switchTenant: '切换公司',
  },
  // ── Navigation actions ──
  nav: {
    chat: '对话',
    home: '首页',
    teamspace: '团队空间',
    ceoDesk: 'CEO 工作台',
    booxCommand: 'Boox 指挥台',
    aiChat: 'AI 对话',
    brainMonitor: 'Brain 监控',
    runtimeDebug: 'Runtime 调试',
    alertCenter: '告警中心',
    rolloutDashboard: '发布看板',
    revenueInbox: '营收邮箱',
    messagingIntake: '消息入口',
    surfaceAccess: 'Surface Access',
    partnerApplications: 'Partner 申请',
    onboardingControl: 'Onboarding 控制台',
    onboardingRecords: 'Onboarding 记录',
    assignmentPolicies: '分配策略',
    capabilityMatrix: '能力矩阵',
    stateConsole: '状态控制台',
    eInvoicingOps: '电子发票运营',
    strategy: '战略',
    fundraising: '融资',
    dataRoom: '资料室',
    aiCoCeo: 'AI 联席 CEO',
    tenants: '租户',
    globalUsers: '全局用户',
    userRoles: '用户角色',
    authControl: '认证控制',
    capabilities: '能力',
    policies: '策略',
    exceptionPolicies: '异常策略',
    workflowPolicies: '工作流策略',
    whatsAppAdmin: 'WhatsApp',
    builder: '构建器',
    businessPackages: '业务包',
    platformSystems: '平台系统',
    configCompiler: '配置编译器',
    configVersions: '配置版本',
    domainConfigs: '领域配置',
    menuConfig: '菜单配置',
    bridges: '桥接',
    dataTransfer: '数据迁移',
    deployControl: '部署控制',
    printCenter: '打印中心',
    platformAnalytics: '平台分析',
    logExplorer: '日志查询',
    schemaEditor: '模型编辑器',
    dslDesigner: 'DSL 设计器',
    viewTemplates: '视图模板',
    documentTemplates: '文档模板',
    workflows: '工作流',
    workflowTemplates: '工作流模板',
    scripts: '脚本',
    aiCommandCenter: 'AI 指挥中心',
    aiTeam: 'AI 团队',
    agentLab: 'Agent Lab',
    skills: '技能',
    skillConfig: '技能配置',
    prompts: '提示词',
    trainingData: '训练数据',
    memory: '记忆',
    tools: '工具',
    aiControlPlane: 'AI 控制平面',
    brainLogs: 'Brain 日志',
    brainSoul: 'Brain Soul',
    brainSchedule: 'Brain 调度',
    recurringTasks: '周期任务',
    taskCenter: '任务中心',
    executiveNudges: '高层提醒',
    apiCenter: 'API 中心',
    accessLog: '访问日志',
    userPerformance: '用户表现',
    jobTimeline: '任务时间线',
    pro2: {
      importShipment: '进口货件',
      importShipments: '进口货件',
      importContainer: '进口柜',
      importContainers: '进口柜',
      importCargo: '进口货物',
      exportShipment: '出口货件',
      exportShipments: '出口货件',
      exportBooking: '出口订舱',
      exportBookings: '出口订舱',
      exportContainer: '出口柜',
      exportContainers: '出口柜',
      exportCargo: '出口货物',
      invCostNote: '发票 / 成本单',
      invCostNotes: '发票 / 成本单',
      chargeLine: '费用明细',
      chargeLines: '费用明细',
      company: '公司资料',
      companies: '公司资料',
    },
    // Home
    Dashboards: '仪表盘',
    'Team Monitor': '团队监控',
    Shortcuts: '快捷方式',
    Recent: '最近',
    // Inbox
    'All Messages': '所有消息',
    Emails: '邮件',
    WhatsApp: 'WhatsApp',
    'My Tasks': '我的任务',
    Approvals: '审批',
    Alerts: '通知',
    // Business
    Orders: '订单',
    Quotations: '报价单',
    Pipelines: '流水线',
    Goals: '目标',
    'Purchase Orders': '采购单',
    'Vendor Sourcing': '供应商寻源',
    Customers: '客户',
    Vendors: '供应商',
    Contacts: '联系人',
    'Sales Reports': '销售报表',
    // Transport
    Jobs: '工单',
    Planner: '规划',
    'FCL Planner': '整箱规划',
    'LCL Planner': '拼箱规划',
    Trips: '行程',
    'Live Tracking': '实时追踪',
    Dispatch: '调度',
    'Master Data': '主数据',
    Drivers: '司机',
    Vehicles: '车辆',
    Routes: '路线',
    Billing: '计费',
    // Freight
    'Sea Imports': '海运进口',
    'Sea Exports': '海运出口',
    'Air Imports': '空运进口',
    'Air Exports': '空运出口',
    'Port System': '港口系统',
    CFS: 'CFS',
    Customs: '报关',
    Documents: '文件',
    Rates: '运价',
    Carriers: '承运人',
    Vessels: '船舶',
    Accounting: '会计',
    'Volume Reports': '运量报表',
    // Warehouse
    Inbound: '入库',
    Inventory: '库存',
    Outbound: '出库',
    'Work Orders': '工单',
    Warehouses: '仓库',
    Locations: '库位',
    'Stock Reports': '库存报表',
    Movements: '移动',
    // Finance
    Invoices: '发票',
    Receipts: '收据',
    'AR Aging': '应收账龄',
    'Vendor Bills': '供应商账单',
    Payments: '付款',
    'Journal Entries': '记账凭证',
    'Chart of Accounts': '会计科目',
    'Bank Account': '银行账户',
    'Bank Accounts': '银行账户',
    Statement: '对账单',
    'Bank Statements': '银行对账单',
    Reconciliation: '对账',
    'Charge Code': '费用代码',
    'Charge Codes': '费用代码',
    'Tax Jurisdiction': '税务辖区',
    'Tax Jurisdictions': '税务辖区',
    'Tax Codes': '税码',
    'Tax Rule': '税务规则',
    'Tax Rules': '税务规则',
    'P&L Reports': '损益报表',
    'Balance Sheets': '资产负债表',
    Shipment: '货件',
    Shipments: '货件',
    Overview: '概览',
    Accounts: '账户',
    Bookings: '订舱',
    Documentation: '单证',
    Release: '放货',
    'Local Services': '本地服务',
    Schedules: '船期',
    'Principal Tasks': '总代任务',
    'AI Intake': 'AI 收件',
    Yard: '堆场',
    Moves: '场内调度',
    Maintenance: '维修',
    Exceptions: '异常',
    Releases: '放箱',
    Repairs: '维修',
    Container: '集装箱',
    Containers: '集装箱',
    Cargo: '货物',
    Costing: '成本',
    Import: '进口',
    Export: '出口',
    Transhipment: '中转',
    'Cross Trade': '三角贸易',
    Local: '本地',
    Consol: '拼箱',
    Air: '空运',
    'Shipment Report': '货件报表',
    'Billing Report': '计费报表',
    'Imp Shipment': '进口货件',
    'Imp Container': '进口箱',
    'Imp Cargo': '进口货物',
    'Exp Shipment': '出口货件',
    'Exp Booking': '出口订舱',
    'Exp Container': '出口箱',
    'Exp Cargo': '出口货物',
    'Inv / Cost Note': '发票 / 成本单',
    'Charge Lines': '费用行',
    'Quotation / Rates': '报价 / 运价',
    'Job Order': '作业单',
    'Job Orders': '作业单',
    'By Customer': '按客户',
    'By Date': '按日期',
    'Driver Log': '司机日志',
    'Driver Pay': '司机工资',
    // Workforce
    Employees: '员工',
    Departments: '部门',
    Branches: '分公司',
    Roles: '角色',
    Permissions: '权限',
    // Settings
    'General Settings': '通用设置',
    'My Profile': '我的资料',
    'Company Settings': '公司设置',
    Team: '团队',
    // ── CC（指挥中心）导航项 ──
    Home: '首页',
    Tenants: '租户',
    'Users & Roles': '用户与角色',
    Capabilities: '能力配置',
    'Capability Matrix': '能力矩阵',
    'WhatsApp Config': 'WhatsApp 配置',
    'Menu Config': '菜单配置',
    'Domain Configs': '域配置',
    'Auth Control': '认证管控',
    'Command Center': '指挥中心',
    'AI Team': 'AI 团队',
    Skills: '技能',
    'Skill Config': '技能配置',
    'Rules & Policies': '规则与策略',
    'Exception Policies': '异常策略',
    'Workflow Policies': '工作流策略',
    Prompts: '提示词',
    'Training Data': '训练数据',
    'Memory Manager': '记忆管理',
    'Brain Monitor': 'Brain 监控',
    'Brain Logs': 'Brain 日志',
    'Brain Soul': 'Brain 灵魂',
    'Brain Schedule': 'Brain 调度',
    'Thread Logs': '线程日志',
    'Agent Lab': '智能体实验室',
    Messaging: '消息',
    'Email Logs': '邮件日志',
    'Email Accounts': '邮箱账户',
    'WhatsApp Groups': 'WhatsApp 群组',
    'Platform Admin': '平台管理',
    'Rollout Dashboard': '发布仪表盘',
    'Deploy Control': '部署管控',
    'AI Control Plane': 'AI 管控面',
    'Bridge Registry': 'Bridge 注册表',
    'Tool Registry': '工具注册表',
    'Doc Templates': '文档模板',
    'Schema Editor': 'Schema 编辑器',
    Workflows: '工作流',
    'WF Templates': '工作流模板',
    Scripts: '脚本',
    'Config Versions': '配置版本',
    'Config Compiler': '配置编译器',
    'View Templates': '视图模板',
    'DSL Designer': 'DSL 设计器',
    'Print Center': '打印中心',
    // ── CC 额外标签（匹配 MODULE_NAV_ITEMS labelPlural）──
    Rollout: '功能发布',
    'AI Chat': 'AI 对话',
    'Global Users': '全局用户',
    'User Roles': '用户角色',
    Policies: '策略',
    Schedule: '调度',
    Tools: '工具',
    Memory: '记忆',
    'Email Intake': '邮件接入',
    'Log Explorer': '日志浏览器',
    'Job Timeline': '工单时间线',
    Bridges: '桥接',
    'Email Inbox': '邮件收件箱',
    'Platform KPI': '平台 KPI',
    'By Company': '按公司',
    'By Region': '按区域',
    'By Port Pair': '按港口对',
    'Haulage Live': '拖车实时',
    'Freight Volume': '货运量',
    'WMS Throughput': '仓储吞吐',
    'Growth & Churn': '增长与流失',
    'AI Efficiency': 'AI 效率',
    // ── Driver PWA nav ──
    'My Trips': '我的行程',
    'My Pay': '我的薪资',
    Driver: '司机',
    // ── Module nav items (used by MODULE_NAV_ITEMS in sidebar) ──
    // Home
    Dashboard: '仪表盘',
    Today: '今日',
    Inbox: '收件箱',
    Email: '邮件',
    'AI Review': 'AI 审核',
    Directory: '企业目录',
    Connections: '我的连接',
    Messages: '消息',
    Channels: '频道',
    // Business (OMS)
    Pipeline: '商机管道',
    'Business Party': '商业伙伴',
    'Business Parties': '商业伙伴',
    Customer: '客户',
    Circles: '圈子',
    Group: '集团',
    Groups: '集团',
    RFQ: '询价',
    // Transport (TMS)
    'Event Log': '事件日志',
    // Haulage (HMS)
    'Control Room': '控制室',
    'AI CS Manager': 'AI 客服经理',
    'Staff Performance': '员工绩效',
    'TR128 Audit': 'TR128 审核',
    'CO2e Report': '碳排放报告',
    // Freight (FMS)
    'Sea Schedule': '船期表',
    // PRO2
    'Import Shipment': '进口货件',
    'Import Shipments': '进口货件',
    'Import Container': '进口柜',
    'Import Containers': '进口柜',
    'Import Cargo': '进口货物',
    'Export Shipment': '出口货件',
    'Export Shipments': '出口货件',
    'Export Booking': '出口订舱',
    'Export Bookings': '出口订舱',
    'Export Container': '出口柜',
    'Export Containers': '出口柜',
    'Export Cargo': '出口货物',
    'Invoice / Cost Note': '发票 / 成本单',
    'Invoice / Cost Notes': '发票 / 成本单',
    'Charge Line': '费用明细',
    Company: '公司',
    Companies: '公司',
    // Supply Chain (SCM)
    'Sales Order': '销售订单',
    'Sales Orders': '销售订单',
    Quotation: '报价单',
    Salesperson: '销售员',
    Salespersons: '销售员',
    'Purchase Order': '采购订单',
    Supplier: '供应商',
    Suppliers: '供应商',
    Product: '产品',
    Products: '产品',
    'Stock Movement': '库存移动',
    'Stock Movements': '库存移动',
    Fulfillment: '履约',
    'Shipment Tracking': '物流追踪',
    Reports: '报表',
    // Service (SVC)
    'Service Order': '服务工单',
    'Service Orders': '服务工单',
    Equipment: '设备',
    Crew: '班组',
    Crews: '班组',
    Sites: '站点',
    // Warehouse (WMS)
    Tasks: '任务',
    'QC / Inspection': '质检 / 验收',
    'Warehouse Tasks': '仓库任务',
    'Arrival Dock': '到货月台',
    PO: '采购单',
    'Material Request': '领料申请',
    'Material Requests': '领料申请',
    'Material Return': '物料退回',
    'Inventory Transfer': '库存转移',
    'Inventory Return': '库存退回',
    'Shared Documents': '共享文档',
    '3PL Management': '3PL 管理',
    Supervisor: '主管看板',
    'Executive KPI': '高管 KPI',
    'CS Workbench': '客服工作台',
    // Finance
    'Accounts Receivable': '应收账款',
    'Accounts Payable': '应付账款',
    'Journal Entry': '记账凭证',
    'AR Collection': '应收催收',
    'Payment Run': '付款批次',
    'Payment Runs': '付款批次',
    'AP Aging': '应付账龄',
    'Statement of A/c': '对账单',
    'Statements of A/c': '对账单',
    'Tax Report': '税务报表',
    'Tax Reports': '税务报表',
    'General Ledger': '总账',
    'Trial Balance': '试算表',
    'Profit & Loss': '损益表',
    'Balance Sheet': '资产负债表',
    'Cash Flow': '现金流量',
    'Management A/c': '管理报表',
    'Management Accounts': '管理报表',
    'Audit Report': '审计报告',
    'Audit Reports': '审计报告',
    'Process Audit': '流程审计',
    'Process Audits': '流程审计',
    Consolidation: '合并报表',
    'E-Invoicing': '电子发票',
    'Audit Overview': '审计概览',
    Settlement: '结算',
    Disputes: '争议',
    'Bill Audit': '账单审核',
    'Bill Audits': '账单审核',
    'Accounting Periods': '会计期间',
    'Tax Config': '税务配置',
    'Exchange Rates': '汇率',
    'Legal Entities': '法律主体',
    // Workforce (HRM)
    Attendance: '考勤',
    Payroll: '薪资',
    'Leave Types': '假期类型',
    'Leave Mgmt': '假期管理',
    Holidays: '公共假日',
    'Holiday Calendar': '假日日历',
    Loans: '借款',
    'Claims Approval': '报销审批',
    Safety: '安全',
    Incidents: '事故',
    Checklists: '检查清单',
    Signoffs: '签到确认',
    Training: '培训',
    Briefings: '安全简报',
    'Safety Briefings': '安全简报',
    'Work Summaries': '工作总结',
    'My Leave': '我的假期',
    'My Attendance': '我的考勤',
    'My Payslip': '我的工资条',
    'My Loans': '我的借款',
    'My Claims': '我的报销',
    // Settings
    General: '通用',
    Profile: '个人资料',
    'PWA Settings': 'PWA 设置',
    'PWA Install Policies': 'PWA 安装策略',
    'Surface Access': '界面访问',
    'Team Members': '团队成员',
    Templates: '模板',
    'Document Templates': '文档模板',
    'Knowledge Base': '知识库',
    Builder: '构建器',
    'Builder Studio': '构建器工坊',
    'Business Packages': '业务能力包',
    'Package Exchange': '能力包交换中心',
    "AI Co-CEO": "人工智能联合首席执行官",
    "AI Command Center": "人工智能指挥中心",
    "API Center": "原料药中心",
    "AR/AP Netting": "AR/AP 净额结算",
    "Access Log": "访问日志",
    "Activities": "活动",
    "Admin Setup": "管理员设置",
    "Agency Accounts": "代理账户",
    "Alert Center": "警报中心",
    "Alliance": "联盟",
    "Alliances": "联盟",
    "Amber": "琥珀色",
    "Appraisal": "鉴定",
    "Assignment Policies": "分配政策",
    "Bahasa Indonesia": "印度尼西亚语",
    "Bahasa Melayu": "马来语",
    "Blue": "蓝色",
    "Boox Command": "布克斯命令",
    "CEO Desk": "总裁办公桌",
    "RM Intelligence Rules": "RM 智能规则",
    "Commercial": "商业",
    "Contracts": "合约",
    "Daily Schedule": "每日日程",
    "Data Room": "资料室",
    "Data Transfer": "数据传输",
    "Design Copilot": "设计副驾驶",
    "E-Invoicing Ops": "电子发票操作",
    "English": "英语",
    "Executive Nudges": "行政推动",
    "Feeder Bookings": "代理作业",
    "Finance": "金融",
    "Financial Setup": "财务设置",
    "Fixed Assets": "固定资产",
    "Fundraising": "筹款",
    "Gate": "门",
    "Green": "绿色",
    "Incentives": "激励措施",
    "Indigo": "靛蓝",
    "Inspection": "检查",
    "Issue Orders": "发出指令",
    "Kudos Wall": "荣誉墙",
    "LLM Intake Test": "法学硕士入学考试",
    "MCP Management": "MCP管理",
    "Messaging Intake": "消息接收量",
    "Onboarding Assignment Policies": "入职分配政策",
    "Onboarding Control": "入职控制",
    "Onboarding Records": "入职记录",
    "Part": "部分",
    "Parts": "零件",
    "Partner Applications": "合作伙伴应用",
    "Payroll & Compliance": "薪资与合规",
    "Performance Appraisals": "绩效考核",
    "Pink": "粉色",
    "Platform Analytics": "平台分析",
    "Platform Systems": "平台系统",
    "Port Call Docs": "端口调用文档",
    "Pricing": "定价",
    "Profitability": "盈利能力",
    "Purple": "紫色",
    "REST API": "REST API",
    "Re-use Leads": "重复利用潜在客户",
    "Recovery": "恢复",
    "Recurring Tasks": "重复任务",
    "Revenue": "收入",
    "Revenue Inbox": "收入收件箱",
    "Rose": "玫瑰",
    "Runtime Debug": "运行时调试",
    "Safety Hub": "安全中心",
    "Schedule Board": "日程表",
    "Settings": "设置",
    "Setup": "设置",
    "Shift Swap Requests": "轮班调换请求",
    "Shift Swaps": "轮班互换",
    "Site Ops": "现场运营",
    "State Console": "状态控制台",
    "Stock Ledger": "库存分类帐",
    "Strategy": "策略",
    "Tally Control": "计数控制",
    "Tariff": "关税",
    "Task Center": "任务中心",
    "Tax Configuration": "税务配置",
    "Teal": "青色",
    "Teamspace": "团队空间",
    "Tiếng Việt": "Tiếng Việt",
    "Tyre": "轮胎",
    "Tyres": "轮胎",
    "User Performance": "用户表现",
    "Vehicle": "车辆",
    "WMS": "WMS",
    "WMS Setup": "仓库管理系统设置",
    "Workflow Templates": "工作流程模板",
    "ไทย": "ไทย",
    "中文": "中文",
    "日本語": "日本語",
    "繁體中文": "繁體中文",
  },

  networkPage: {
    common: {
      close: '关闭',
      cancel: '取消',
      refresh: '刷新',
      unknown: '未知',
    },
    circleTypes: {
      ownership: '所有权集团',
      agent_network: '代理圈',
      vendor_panel: '供应商面板',
      peer_circle: '同行互助圈',
      consortium: '联营体',
    },
    groupEntityTypes: {
      hq: '总部',
      subsidiary: '子公司',
      branch: '分支',
      associate: '联营',
      default: '子公司',
    },
    circles: {
      memberCount: '{{count}} 成员',
      owner: '我是圈主',
      admin: '管理员',
      detailLoadFailed: '加载圈子详情失败',
      nameRequired: '请输入圈子名称',
      createdSuccess: '圈子创建成功',
      createFailed: '创建失败',
      listLoadFailed: '加载圈子列表失败',
      createTitle: '创建新圈子',
      nameLabel: '圈子名称 *',
      namePlaceholder: '例如: CMS 代理网络',
      typeLabel: '圈子类型 *',
      descLabel: '描述（可选）',
      descPlaceholder: '简要说明这个圈子的用途',
      createSubmitting: '创建中...',
      createAction: '创建圈子',
      title: 'Trade Networks 与 Circles',
      subtitle: '把唯一归属的集团，与可重叠的贸易网络、协作圈层分开管理',
      ownershipNoteBadge: 'Ownership Group',
      ownershipNoteDesc: '每家公司只能属于一个集团，这层关系单独管理，不和 trade network / circle 混在一起。',
      openGroupPage: '打开集团页',
      emptyTitle: '还没有加入任何圈子',
      emptyDesc: '创建第一个圈子，开始与合作伙伴协作',
      typeCount: '{{count}} 个',
      membersTitle: '成员 ({{count}})',
      tradeNetworksTitle: 'Trade Networks',
      tradeNetworksDesc: '可重叠的代理网络、联营网络、co-loader 网络与业务联盟。',
      tradeNetworksEmptyDesc: '还没有 trade network。可建立 CMS Group Network、Portever Group Network、E-Freight Global Network 这类可重叠业务网络。',
      circlesSectionTitle: 'Circles',
      circlesSectionDesc: '更小、更本地化的协作圈，例如 trucker circle、transporter circle、coloader circle、friend-based circle。',
      circlesSectionEmptyDesc: '还没有 circle。可建立供应商、车队、同行或朋友协作圈。',
    },
    groups: {
      current: '当前',
      switch: '切换',
      loadFailed: '加载集团信息失败',
      switchSuccess: '已切换工作台，即将刷新…',
      switchFailed: '切换失败，请重试',
      title: 'Ownership Group',
      subtitle: '每家公司只能属于一个集团。这里显示成员公司、HQ、office base，并支持在集团内切换工作台。',
      emptyTitle: '您的账户尚未加入任何集团',
      emptyDesc: '联系平台管理员将您的公司纳入集团管理',
      summary: '所有权集团 · {{count}} 家成员公司',
      roleFallback: '成员',
      exclusiveBadge: '唯一归属',
      hqLabel: 'HQ',
      hqCompany: '总部公司',
      memberCompanies: '成员公司数',
      coverage: '覆盖国家数',
      companyMembersTitle: '集团成员公司',
      adminMembersTitle: '集团管理员与成员 ({{count}})',
      officeBaseValue: '{{country}} · {{city}}',
      timezoneValue: '时区 {{timezone}}',
      currencyValue: '基础币种 {{currency}}',
      noOfficeBase: '办公室地点待补充',
    },
    rfq: {
      pageTitle: 'RFQ 工作台',
      pageSubtitle: '发起询价或查看收到的报价请求',
      creator: '我发起',
      detailLoadFailed: '加载 RFQ 详情失败',
      publishSuccess: 'RFQ 已发布，等待报价',
      publishFailed: '发布失败',
      quoteAmountRequired: '请输入报价金额',
      quoteSubmitSuccess: '报价已提交',
      quoteSubmitFailed: '提交失败',
      titleRequired: '请输入 RFQ 标题',
      draftCreated: 'RFQ 草稿已创建',
      createFailed: '创建失败',
      listLoadFailed: '加载 RFQ 列表失败',
      typeSpot: '现货询价',
      typeContract: '合同询价',
      typeRecurring: '周期询价',
      modeSealed: '密封报价',
      modeOpen: '公开报价',
      modeMixed: '混合模式',
      tabInfo: '基本信息',
      tabQuotes: '报价',
      tabSubmit: '提交报价',
      fieldType: '询价类型',
      fieldCurrency: '货币',
      fieldDeadline: '截止日期',
      fieldCreatedAt: '创建时间',
      cargoSummary: '货物摘要',
      publishSubmitting: '发布中...',
      publishAction: '发布 RFQ，开始收集报价',
      inviteesTitle: '受邀方 ({{count}})',
      emptyQuotes: '尚无报价',
      awarded: '已授标',
      submittedAt: '提交于 {{date}}',
      quoteAmountLabel: '报价金额 ({{currency}}) *',
      quoteNotesLabel: '备注（可选）',
      quoteNotesPlaceholder: '报价条款、注意事项等',
      submitSubmitting: '提交中...',
      submitAction: '提交报价',
      createTitle: '发起新 RFQ',
      formTitleLabel: '询价标题 *',
      formTitlePlaceholder: '例如: FCL Haulage Q2 2026 - TANJONG PAGAR',
      formTypeLabel: '询价类型',
      formModeLabel: '报价模式',
      formCircleLabel: '关联圈子（可选）',
      formCirclePlaceholder: '不限制，向全网开放',
      formCircleAny: '不限制，向全网开放',
      formDeadlineLabel: '报价截止时间',
      formDescriptionLabel: '背景描述（可选）',
      formDescriptionPlaceholder: '货物信息、服务要求等补充说明',
      createSubmitting: '创建中...',
      createAction: '创建草稿',
      tabMine: '我发起的',
      tabReceived: '收到的',
      emptyMineTitle: '还没有发起过询价',
      emptyReceivedTitle: '没有收到任何询价请求',
      emptyMineDesc: '向供应商发起询价，快速获取报价',
      startAction: '发起 RFQ',
      statuses: {
        draft: '草稿',
        published: '已发布',
        quoting: '报价中',
        evaluating: '评标中',
        awarded: '已授标',
        completed: '已完成',
        cancelled: '已取消',
        expired: '已过期',
      },
    },
  },

  crmAccount360: {
    health: {
      attention: '需关注',
      active: '活跃',
      steady: '稳定',
    },
    title: 'RM 客户 360',
    description: '汇总联系人、商机、跟进、报价与作业的商业全景。',
    askBoox: '询问 Boox',
    loading: '正在加载 RM 客户全景...',
    loadFailed: '加载 RM 客户 360 失败',
    promptReviewCustomer: '请分析客户 {{name}}，总结当前 pipeline、逾期跟进、最新报价，以及下一步最优商业动作。',
    unnamedContact: '未命名联系人',
    primary: '主要联系人',
    decisionMaker: '决策人',
    roleNotSet: '职位未设置',
    noContactChannel: '暂无联系方式',
    preferred: '首选沟通方式：{{channel}}',
    keyContacts: '关键联系人',
    noContacts: '还没有 RM 联系人。',
    pipelineFollowUp: '商机与跟进',
    noOpenActivity: '当前账户暂无进行中的 RM 活动。',
    expectedClose: '预计成交 {{date}}',
    due: '到期 {{date}}',
    quotesJobs: '报价与作业',
    noQuotesJobs: '还没有关联报价或作业。',
    draft: '草稿',
    validUntil: '有效期至 {{date}}',
    jobTypeFallback: '作业',
    eta: '预计到达 {{date}}',
    cards: {
      openDeals: '进行中商机',
      weighted: '加权后 {{value}}',
      pipelineValue: 'Pipeline 金额',
      staleDeals: '滞后商机 {{count}}',
      nextFollowUp: '下一次跟进',
      overdue: '逾期 {{count}}',
      openQuotesJobs: '未结报价 / 作业',
      lastTouch: '最近触达 {{date}}',
    },
    actionsTitle: 'AI 下一步建议',
    actionsDescription: '基于跟进节奏、商机新鲜度、报价和在途作业生成账户级商业建议。',
    actionsEmpty: 'AI 暂未识别到该账户需要优先处理的商业动作。',
    recommendations: {
      loading: '正在加载 AI 优先事项...',
      due: '到期 {{date}}',
      score: '评分 {{score}}',
      openAccount360: '打开账户 360',
      priority: {
        critical: '关键',
        high: '高',
        medium: '中',
        low: '低',
      },
      summary: {
        critical: '{{count}} 个关键',
        high: '{{count}} 个高优先级',
        medium: '{{count}} 个中优先级',
      },
    },
  },

  operationalIntelligence: {
    workspaceNotFound: '未找到工作台',
    dataRecords: {
      title: '源数据',
      description: '查看运营智能背后的源数据集，上传 CSV 更新，手动新增行，并打开每个视图背后的实时记录。',
      loadFailed: '加载数据集失败',
      importFailed: '导入数据集失败',
      metrics: {
        datasets: '源数据集',
        records: '实时记录',
        uploadMode: '导入模式',
      },
      uploadModes: {
        replace: '替换数据集',
        append: '追加数据集',
      },
      actions: {
        quickCapture: '快速采集',
        documents: '文档与证据',
        refresh: '刷新数据',
      },
      guidance: {
        title: '源数据指引',
        description: '默认收起指引，保持工作台整洁。',
      },
      datasetCard: {
        records: '{{count}} 条记录',
        fields: '{{count}} 个字段',
        sourceEvidence: '源证据：{{date}}',
        structure: '数据集结构',
        latestSourceEvidence: '最新源证据',
        storedAt: '已于 {{date}} 存入共享运营智能文档工作台',
        openSourceFile: '打开源文件',
        openLiveRecords: '打开实时记录',
        addRow: '新增行',
        importing: '正在导入...',
        uploadCsv: '上传 CSV',
      },
    },
    documents: {
      title: '文档与证据',
      description: '将笔记、照片、投诉文件、保修证据和源数据表上传到共享的运营智能文档工作台。',
      workspaceTitle: '运营智能文档工作台',
      workspaceDescription: '此页面让上传的证据在案例、信号和源数据导入之间保持可复用。',
      backToSourceData: '返回源数据',
      uploadEnabled: '已启用上传',
      summary: {
        aiReady: 'AI 已就绪',
        aiReadyDescription: '当前可见且已带 AI 摘要或已识别用途的文档数',
        draftLinked: '已关联草稿',
        draftLinkedDescription: '当前可见且已创建或关联实时草稿的文档数',
        linkedRecords: '关联记录',
        linkedRecordsDescription: '当前文件列表中，关联到事件、信号、案例和证据链的交叉链接数',
        pendingReview: '待审核',
        pendingReviewDescription: '当前可见且仍待完成处理或待人工进一步审核的文档数',
      },
      whyThisViewExists: '为什么需要这个视图',
      intro: '这里的文档不是被动附件。每个文件都可以携带解析状态、AI 摘要、建议路由，以及跨运营智能流程的关联记录。',
      badges: {
        parseStatus: '内联显示解析状态',
        aiSummary: '内联显示 AI 文档类型与摘要',
        suggestedRoute: '内联显示建议路由',
        linkedRecords: '内联显示关联记录',
      },
    },
    quickCapture: {
      title: '快速采集',
      description: '实时采集一线信号。AI 会对记录进行分流、路由到正确运营流，并在需要时触发告警。',
      loadStatsFailed: '加载信号统计失败',
      requiredFields: '类别、站点和主题为必填项。',
      captureFailed: '采集信号失败',
      formTitle: '一线采集表单',
      formDescription: 'AI 会对该信号进行分类、生成摘要，并在达到阈值时触发告警。',
      openSignalIntake: '打开信号入口',
      pushEnabled: '推送已启用',
      enablePushAlerts: '启用推送告警',
      stats: {
        total: '已采集信号',
        open: '未关闭信号',
        escalated: '已升级',
      },
      attachments: '附件',
      trackedWorkspaceFiles: '已跟踪的工作台文件',
      storedInWorkspace: '已存入共享运营智能文档工作台',
      open: '打开',
      guidance: {
        title: '采集指引',
        description: '默认收起指引，只有需要快速提醒时再展开。',
      },
      sourceData: '源数据',
      submitForAiTriage: '提交给 AI 分流',
      latestCaptureReceipt: '最新采集回执',
      linkedThroughEvidenceChain: '已通过共享工作台证据链关联',
      viewFile: '查看文件',
      noFileEvidence: '该信号提交时未附带文件证据。',
      openCreatedRecord: '打开已创建记录',
      emptyReceipt: '提交一次信号后，最新采集到的文件证据会保留在这里，便于追溯。',
      categoryMix: '类别分布',
      recentSignals: '最近信号',
      noSignals: '尚未采集到信号。',
      pushHintEnabled: '此设备已启用推送。高优先级信号升级可立即推送到这里。',
      pushHintDisabled: '此设备尚未启用推送。可在上方开启，以便实时跟进运营事件。',
    },
  },

  // ── Today page ──
  today: {
    title: '今日',
    tasks: '任务',
    allClear: '今日无待办！',
    allClearDesc: '今天没有需要处理的任务、作业或配送。',
    jobTitle: '{{module}} #{{id}}',
    tripTitle: '行程 {{id}}',
    wmsTaskTitle: '仓库 {{type}} #{{id}}',
    shipmentTitle: '{{type}} {{id}}',
  },

  // ── Module labels (standalone) ──
  module: {
    hms: '旧版集卡 (HMS)',
    tms: '经典运输',
    wms: '仓储流',
    fms: '货运领航',
    scm: '供应链导航',
    svc: '服务',
    hrm: '人力资源',
    oms: '订单',
    acc: '财务',
  },

  // ── SVC (服务管理) 页面级翻译 ──
  svc: {
    status: {
      DRAFT: '草稿', CONFIRMED: '已确认', SCHEDULED: '已排期', DISPATCHED: '已派工',
      IN_PROGRESS: '进行中', COMPLETED: '已完成', INVOICED: '已开票', CANCELLED: '已取消',
      AVAILABLE: '可用', IN_USE: '使用中', MAINTENANCE: '维修中', RETIRED: '已停用',
      ON_DUTY: '在岗', OFF_DUTY: '离岗', ON_LEAVE: '请假', ACTIVE: '活跃', INACTIVE: '停用',
    },
    serviceType: {
      crane_lift: '吊装作业', rigging: '索具作业', transport: '运输', installation: '安装',
      maintenance: '维护保养', inspection: '检查', other: '其他',
    },
    priority: { low: '低', normal: '普通', high: '高', urgent: '紧急' },
    equipmentType: {
      crane: '起重机', forklift: '叉车', truck: '货车', generator: '发电机',
      scaffold: '脚手架', tool_set: '工具套装', other: '其他',
    },
    field: {
      customer: '客户', site: '工地', serviceType: '服务类型', priority: '优先级',
      scheduledDate: '预定日期', estHours: '预计工时', description: '描述', remarks: '备注',
      assetCode: '资产编号', name: '名称', type: '类型', capacity: '容量/吨位', serialNo: '序列号',
      location: '位置', nextServiceDate: '下次维保日期', code: '编号', crewName: '班组名称',
      teamLeader: '组长', skills: '技能', siteCode: '工地编号', siteName: '工地名称',
      contact: '联系人', phone: '电话', postalCode: '邮编', address: '地址',
    },
    kpi: { totalOrders: '工单总数', scheduled: '已排期', inProgress: '进行中', completed: '已完成' },
    tab: {
      recentOrders: '最近工单', activeDispatched: '活跃 / 已派工', allOrders: '全部工单',
      draft: '草稿', active: '活跃', pendingDispatch: '待派工', dispatched: '已派工',
      allEquipment: '全部设备', available: '可用', maintenance: '维修中',
      allCrews: '全部班组', allSites: '全部工地', allInvoices: '全部账单',
      pendingBilling: '待开票', costBreakdown: '成本明细', summary: '汇总',
    },
    col: {
      orderNo: '工单号', customer: '客户', site: '工地', type: '类型', scheduled: '排期',
      status: '状态', crew: '班组', date: '日期', equipment: '设备', priority: '优先级',
      estHrs: '预计工时', created: '创建时间', assetCode: '资产编号', name: '名称', capacity: '容量',
      location: '位置', nextService: '下次维保', lastService: '上次维保', code: '编号',
      crewName: '班组名称', teamLeader: '组长', members: '成员', skills: '技能',
      siteName: '工地名称', address: '地址', contact: '联系人', hours: '工时',
      materials: '材料费', total: '合计', labor: '人工费', totalCost: '总成本',
      revenue: '收入', marginPct: '毛利率', period: '统计周期', orders: '工单数',
      completed: '已完成', cost: '成本', avgHrsJob: '平均工时/单', serialNo: '序列号',
      phone: '电话', postalCode: '邮编',
    },
    action: {
      confirm: '确认', schedule: '排期', dispatch: '派工', startWork: '开始作业',
      complete: '完成', invoice: '开票', cancel: '取消',
      cancelConfirm: '确定取消此服务工单？',
    },
    page: {
      dashboard: '服务管理', orders: '服务工单', dispatch: '派工管理',
      equipment: '设备管理', crew: '班组管理', sites: '工地管理', billing: '服务账单',
      costing: '服务成本', reports: '服务报表',
    },
    dialog: {
      newOrder: '新建服务工单', addEquipment: '添加设备', addCrew: '添加班组', addSite: '添加工地',
    },
  },

  // ── Common ──
  common: {
    loading: '加载中...',
    viewAll: '查看全部',
    more: '更多',
    save: '保存',
    reset: '重置',
  },

  // ── Settings page ──
  settings: {
    desc: '个性化您的 Boox 使用体验。',
    tabs: '标签页',
    clearTabsOnSignout: '退出时清除标签页',
    clearTabsOnSignoutDesc: '开启后，退出登录时将关闭所有标签页。关闭可保留标签页到下次登录。',
    clearTabsEnabled: '退出时将清除标签页',
    clearTabsDisabled: '标签页将跨会话保留',
    voiceCommands: '语音指令',
    voiceCommandsAlwaysOn: 'PWA 常驻语音指令',
    voiceCommandsAlwaysOnDesc: '在 PWA 侧边栏添加麦克风开关，执行指令后继续监听，直到手动关闭。',
    voiceCommandsEnabled: 'PWA 语音指令已启用',
    voiceCommandsDisabled: 'PWA 语音指令已禁用',
    langHint: '立即应用于整个应用。',
    surfaceMenu: '个人快捷菜单',
    surfaceMenuDesc: '选择 PWA 和 Chrome 扩展中显示的菜单类别。',
    surfaceSaved: '个人快捷菜单已保存',
    surfaceSaveFailed: '保存个人菜单失败',
    surfaceReset: '个人快捷菜单已重置',
    surfaceResetFailed: '重置个人菜单失败',
    pwaMenu: 'PWA 菜单',
    pwaMenuDesc: '当前应用于 go-pwa 的个人菜单。',
    extensionMenu: '扩展菜单',
    extensionMenuDesc: '当前应用于 Chrome 扩展的个人菜单。',
    categories: '个类别',
  },

  // ── Accent color ──
  accent: {
    label: '主题色',
  },

  // ── Sidebar ──
  sidebar: {
    collapse: '折叠',
    expand: '展开',
  },

  // ── MDI Tabs ──
  mdi: {
    closeTab: '关闭标签',
    closeOther: '关闭其他',
  },

  // ── Theme ──
  theme: {
    label: '主题',
    languageLabel: '语言',
    light: '浅色',
    dark: '深色',
    system: '系统',
  },

  // ── Forgot Password ──
  forgotPassword: {
    title: '忘记密码',
    desc: '输入您的邮箱，我们将发送重置链接。',
    submit: '发送重置链接',
    sending: '发送中…',
    sentTitle: '请查收邮件',
    sentDesc: '密码重置链接已发送至 {{email}}。',
    resend: '重新发送',
    backToLogin: '返回登录',
    failed: '发送重置邮件失败',
  },

  // ── Reset Password ──
  resetPassword: {
    title: '重置密码',
    desc: '请输入您的新密码。',
    newPassword: '新密码',
    confirmPassword: '确认密码',
    minLength: '最少 8 个字符',
    mismatch: '两次密码不一致',
    tooShort: '密码长度至少 8 个字符',
    submit: '重置密码',
    resetting: '重置中…',
    successTitle: '密码已重置',
    successDesc: '您的密码已成功重置，正在跳转到登录页面…',
    goToLogin: '前往登录',
    failed: '重置密码失败',
    invalidLink: '无效的重置链接',
    invalidLinkDesc: '此重置链接无效或已过期。',
    requestNew: '重新申请',
  },

  // ── Two-Factor Authentication ──
  twoFactor: {
    title: '两步验证',
    emailDesc: '请输入发送至 {{email}} 的 6 位验证码。',
    appDesc: '请输入验证器应用中的 6 位验证码。',
    verify: '验证',
    verifying: '验证中…',
    invalidCode: '验证码无效',
    resend: '重新发送验证码',
    resendIn: '{{seconds}}秒后可重新发送',
    backToLogin: '返回登录',
  },

  // ── Admin panel ──
  admin: {
    title: 'AI 管理',
    groupOps: '运营监控',
    groupSkills: '技能 & 工具',
    groupTraining: 'AI 训练',
    groupConfig: '平台配置',
    domainConfigs: '领域配置',
    menuConfig: '菜单配置',
    userRoles: '用户 & 角色',
    commandCenter: '指挥中心',
    brainMonitor: 'Brain 监控',
    brainSoul: 'Brain Soul',
    brainSchedule: 'Brain 调度队列',
    jobTimeline: 'Job ETA 时间轴',
    agentLab: '自动化任务',
    aiTeam: 'AI 员工团队',
    logViewer: '日志浏览',
    alertsApprovals: '告警 & 审批',
    skillManager: '技能管理',
    skillResources: '技能资源',
    toolRegistry: '工具注册',
    promptEditor: 'Prompt 编辑器',
    trainingData: '训练数据',
    memoryManager: '记忆管理',
    docTemplates: '文档模板',
    configVersions: '配置版本',
    schemaEditor: 'Schema 编辑器',
    workflows: '工作流',
    scriptEditor: '脚本编辑器',
    search: '搜索',
    refresh: '刷新',
    create: '创建',
    save: '保存',
    cancel: '取消',
    delete: '删除',
    edit: '编辑',
    preview: '预览',
    copy: '复制',
    all: '全部',
    required: '必需',
    optional: '可选',
    noData: '无数据',
    prompt: {
      listTitle: 'Prompt 列表',
      searchPlaceholder: '搜索 prompt…',
      noMatch: '无匹配 prompt',
      selectHint: '选择左侧 Prompt 开始编辑',
      active: '活跃',
      noActiveVersion: '无活跃版本',
      unsavedChanges: '未保存更改',
      params: '参数',
      history: '历史',
      rollback: '回滚',
      saveNewVersion: '保存新版本',
      targetModel: '目标模型',
      maxTokens: '最大 Token',
      temperature: 'Temperature',
      variables: '变量 (逗号分隔)',
      editorPlaceholder: '在此编写 prompt 内容…',
      changeNote: '变更说明 (可选)…',
      chars: '字符',
      tokens: 'tokens',
      versionHistory: '版本历史',
      noVersions: '暂无版本记录',
      draft: '草稿',
      archived: '归档',
      load: '加载',
      activate: '激活',
      uses: '{{count}} 次',
      quality: '质量 {{score}}%',
      emailParsingSystem: '📧 邮件解析 (系统)',
      emailParsingUser: '📧 邮件解析 (用户)',
      jobCreationSystem: '🚚 任务创建 (系统)',
      jobCreationUser: '🚚 任务创建 (用户)',
      jobValidationSystem: '✅ 任务验证 (系统)',
      hsCodeSystem: '📦 HS编码 (系统)',
      billingSystem: '💰 计费 (系统)',
      customerInquirySystem: '💬 客户咨询 (系统)',
      seaImportSystem: '🚢 海运进口 (系统)',
      nvoccSystem: '📋 NVOCC拼箱 (系统)',
      dgValidationSystem: '⚠️ 危险品验证 (系统)',
      complianceSystem: '🔒 合规检查 (系统)',
      starterTemplate: '初始模板 — 编辑后保存为 v1',
      // Agent Core
      agentRouter: '🧭 路由 Agent',
      agentDev: '💻 开发 Agent',
      agentOps: '⚙️ 操作 Agent',
      agentData: '📊 数据 Agent',
      agentCoordinator: '🔗 协调 Agent',
      agentSafety: '🛡️ 安全 Agent',
      // Agent Domain
      agentInbox: '📬 收件 Agent',
      agentBilling: '💳 账单 Agent',
      agentCs: '🎧 客服 Agent',
      // Provider
      providerAnthropic: '🟠 Anthropic (Claude)',
      providerOpenai: '🟢 OpenAI (GPT-4)',
      providerGemini: '🔵 Google (Gemini)',
      providerDefault: '⚪ 默认 Provider',
      // Safety
      safetyDatabase: '🗄️ 数据库安全规则',
      safetyTenantIsolation: '🔒 租户隔离规则',
      safetySql: '🛡️ SQL 安全规则',
      // Autonomous
      autonomousBehavior: '🤖 自主行为准则',
      // Document Parsers
      docParserMbl: '📄 MBL 解析器',
      docParserHbl: '📄 HBL 解析器',
      docParserManifest: '📋 舱单解析器',
      docParserNoa: '📢 到港通知解析器',
    },
    skill: {
      catalog: '技能资源目录',
      searchPlaceholder: '搜索技能…',
      noData: '无技能数据',
      selectHint: '选择左侧技能查看资源配置',
      resources: '资源',
      customPoints: '自定义点',
      resourceBinding: '资源绑定',
      customization: '自定义配置',
      runtimePreview: '运行时预览',
      declared: '声明',
      bound: '已绑定',
      isBound: '已绑定',
      notBound: '未绑定',
    },
    training: {
      title: '训练数据管理',
      tabExamples: '训练样本',
      tabEmbeddings: 'Embeddings',
      tabPreferences: '客户偏好',
      searchExamples: '搜索样本…',
      newExample: '新建样本',
      total: '共 {{count}} 条',
      golden: 'Golden',
      approved: '已批准',
      pending: '待审',
      rejected: '已拒绝',
      sampleKey: 'sample-key (唯一标识)',
      description: '描述 (可选)',
      inputLabel: '输入 (JSON or text)',
      outputLabel: '期望输出 (JSON or text)',
      noExamples: '暂无训练样本',
      qualityLabel: '质量',
    },
    memory: {
      title: '记忆管理',
      subtitle: '3层记忆架构',
      taskMemory: '任务记忆',
      workspaceMemory: '工作区记忆',
      orgMemory: '组织记忆',
      searchPlaceholder: '搜索记忆 key 或内容…',
      records: '{{count}} 条记录',
      noRecords: '暂无记忆记录',
      noRecordsHint: 'Agent 运行时会自动产生记忆条目',
      expired: '已过期',
      latest: '最近',
      value: '值',
      metadata: '元数据',
    },
    docTemplate: {
      title: '文档模板管理',
      templates: '{{count}} 模板',
      newTemplate: '新建模板',
      searchPlaceholder: '搜索模板…',
      invoiceTemplate: '发票模板',
      quotationTemplate: '报价单模板',
      blTemplate: 'BL 模板',
      doTemplate: 'DO 模板',
      soaTemplate: 'SOA 模板',
      emailTemplate: '邮件模板',
      receiptTemplate: '收据模板',
      manifestTemplate: '清单模板',
      newDocTemplate: '新建文档模板',
      skillId: '技能 ID',
      resourceKey: '资源 Key',
      type: '类型',
      format: '格式',
      noTemplates: '暂无文档模板',
      noTemplatesHint: '点击「新建模板」添加技能文档模板',
    },
    tool: {
      title: '工具注册中心',
      toolsSkills: '{{count}} 工具/技能',
      newDynamicSkill: '新建动态技能',
      searchPlaceholder: '搜索技能/工具…',
      nativeLabel: '原生',
      dynamicLabel: '动态',
      promptDriven: 'Prompt 驱动',
      toolComposed: '工具编排',
      sandboxBundle: '沙箱脚本',
      promptDrivenDesc: 'LLM 按 prompt 模板生成结构化输出',
      toolComposedDesc: '组合现有工具 ID 链条执行',
      sandboxBundleDesc: 'Python/JS 沙箱执行自定义逻辑',
      slug: 'ID (slug)',
      name: '名称',
      riskLevel: '风险等级',
      descriptionLabel: '描述',
      intents: '意图 (逗号分隔)',
      promptKey: 'Prompt Key',
      toolIdChain: '工具 ID 链 (逗号分隔)',
      model: '模型',
      dynamicSkills: '动态技能',
      nativeSkills: '原生技能',
      enabled: '已启用',
      disabled: '已禁用',
      native: '原生',
      dynamic: '动态',
      intentsLabel: '意图',
      permissions: '权限',
      none: '无',
      toolChain: '工具链',
      retry: '重试',
      escalation: '升级',
      version: '版本',
      noData: '暂无工具/技能',
    },
    configVer: {
      title: '配置版本管理',
      versions: '{{count}} 个版本',
      defaultTemplate: '默认模板',
      editPublish: '编辑并发布',
      finalChannel: '正式通道',
      experimentalChannel: '实验通道',
      currentConfig: '当前生效配置',
      defaultLabel: '默认',
      cached: '缓存',
      tenant: '租户',
      layoutVersion: '布局版本',
      publishTime: '发布时间',
      signature: '签名',
      menuItems: '菜单项',
      pages: '页面',
      featureFlags: 'Feature Flags',
      noConfig: '暂无配置',
      publish: '发布',
      publishing: '发布中…',
      rollbackTo: '回滚到此版本',
      versionHistory: '版本历史',
      noVersionHistory: '暂无版本历史',
      latest: '最新',
      current: '当前',
      publishNewVersion: '发布新配置版本',
      publishNote: '发布后将自动签名并递增版本号',
    },
    // 用户行为分析
    userPerf: {
      title: '用户行为分析',
      subtitle: '功能采用率 · AI 采用率 · 风险用户预警',
      lastRefresh: '最后刷新',
      totalUsers: '总用户数',
      activeUsers: '活跃用户',
      activeUsersSub: '最近 7 天有操作',
      riskUsers: '风险用户 ⚠',
      riskUsersSub: '7 天未活跃',
      avgAiAdoption: '平均 AI 采用率',
      avgAiAdoptionSub: 'AI操作/总操作',
      totalOps: '总业务操作',
      featureHeat: '功能采用热度 Top 5',
      recentEvents: '最近业务事件',
      aiDistribution: 'AI 采用率分布',
      aiHigh: '高采用 ≥20%',
      aiMid: '中采用 5-20%',
      aiLow: '低采用 <5%',
      userTable: '用户绩效明细',
      tableSortHint: '（点击列标题排序）',
      colUser: '用户',
      colTotalOps: '操作总数',
      colFeatures: '功能多样性',
      colActiveDays: '活跃天数',
      colAiAdoption: 'AI 采用率',
      colLastActive: '最后活跃',
      colStatus: '状态',
      statusActive: '活跃',
      statusInactive: '⚠ 7天未活跃',
      noData: '暂无数据',
      days: '{{count}} 天',
      activeDays: '{{count}} 天',
      periodPrefix: '过去',
      justNow: '刚刚',
      minutesAgo: '{{count}} 分钟前',
      hoursAgo: '{{count}} 小时前',
      daysAgo: '{{count}} 天前',
      last7: '过去 7 天',
      last14: '过去 14 天',
      last30: '过去 30 天',
      last60: '过去 60 天',
    },
  },

  flm: {
    page: {
      dashboard: '车队看板',
      assets: '车辆',
      workshop: '维修工单',
      parts: '备件库存',
      tyres: '轮胎',
      costing: '车队成本',
      reports: '车队报表',
    },
    desc: {
      dashboard: '查看车辆可用性、维修负荷、备件预警、轮胎状态和本月车队成本。',
      assets: '管理货车、厢式车、拖头、挂车、车架和支持设备的车队资产主档。',
      workshop: '管理服务请求和维修工单，跟踪停机时间和维修成本。',
      parts: '管理维修备件库存、补货线、单位成本和领用记录。',
      tyres: '跟踪轮胎从库存、安装、维修、翻新到报废的完整生命周期。',
      costing: '按车辆和期间归集维修、轮胎、燃油和固定成本。',
      reports: '查看每公里成本、停机时间、库存流水、轮胎寿命和合规异常报表。',
    },
    action: { refresh: '刷新', create: '创建', record: '记录', close: '关闭', send: '发送', notifyDriver: '通知司机', resend: '重发上次' },
    error: { load: '加载 FLM 数据失败', save: '保存 FLM 动作失败' },
    loading: '正在加载 FLM 记录...',
    empty: '暂无{{label}}记录。FLM 基础结构已准备好接入租户数据。',
    table: {
      vehicles: '车辆',
      workOrders: '工单',
      parts: '备件',
      tyres: '轮胎',
      costing: '成本分摊',
    },
    col: {
      plate: '车牌',
      type: '类型',
      makeModel: '品牌 / 型号',
      status: '状态',
      odometer: '里程',
      workOrder: '工单',
      vehicle: '车辆',
      cost: '成本',
      part: '备件',
      code: '编码',
      stock: '库存',
      reorder: '补货线',
      unitCost: '单位成本',
      serial: '序列号',
      size: '规格',
      position: '位置',
      date: '日期',
      category: '类别',
      source: '来源',
      amount: '金额',
      action: '操作',
    },
    form: {
      assetTitle: '创建车辆',
      serviceRequestTitle: '登记服务请求',
      workOrderTitle: '创建维修工单',
      partTitle: '创建备件',
      stockTitle: '记录库存流水',
      tyreTitle: '创建轮胎',
      tyreEventTitle: '记录轮胎事件',
      fuelTitle: '记录燃油成本',
      complianceTitle: '登记合规记录',
      notifyDriverTitle: '通知司机',
      plateNo: '车牌号',
      assetName: '资产名称',
      assetType: '资产类型',
      odometer: '里程 km',
      vehicle: '车辆',
      selectVehicle: '选择车辆',
      serviceRequest: '服务请求',
      selectServiceRequest: '选择服务请求',
      requestType: '请求类型',
      workType: '工单类型',
      priority: '优先级',
      vendorCost: '外修成本',
      miscCost: '杂项成本',
      diagnosis: '诊断 / 工作范围',
      description: '说明',
      partCode: '备件编码',
      partName: '备件名称',
      qty: '数量',
      reorder: '补货线',
      unitCost: '单位成本',
      part: '备件',
      selectPart: '选择备件',
      movement: '库存动作',
      workOrder: '工单',
      selectWorkOrder: '选择工单',
      serialNo: '轮胎序列号',
      brand: '品牌',
      tyreSize: '轮胎规格',
      purchaseCost: '采购成本',
      tyre: '轮胎',
      selectTyre: '选择轮胎',
      eventType: '事件类型',
      position: '位置',
      costAmount: '成本金额',
      litres: '升数',
      unitPrice: '单价',
      vendor: '供应商',
      complianceType: '合规类型',
      expiryDate: '到期日',
      complianceStatus: '合规状态',
      notificationType: '通知类型',
      amount: '金额',
      note: '备注',
    },
    success: {
      asset: '车辆已创建',
      serviceRequest: '服务请求已创建',
      workOrder: '工单已创建',
      part: '备件已创建',
      stock: '库存流水已记录',
      tyre: '轮胎已创建',
      tyreEvent: '轮胎事件已记录',
      fuel: '燃油成本已记录',
      compliance: '合规记录已创建',
      notifyDriver: '司机通知已发送',
      resendDriver: '司机通知已重发',
      close: '工单已关闭',
    },
    assetType: {
      lorry: '货车',
      van: '厢式车',
      prime_mover: '拖头',
      trailer: '挂车',
      forklift: '叉车',
      other: '其他',
    },
    workType: {
      service: '保养',
      repair: '维修',
      breakdown: '故障',
      tyre: '轮胎',
      inspection: '检查',
      other: '其他',
    },
    requestType: {
      defect: '缺陷',
      service_due: '到期保养',
      breakdown: '故障',
      tyre: '轮胎',
      compliance: '合规',
      other: '其他',
    },
    notificationType: {
      repair_completed: '维修完成',
      extra_cost_settlement: '额外费用结算',
      workshop_update: '车间更新',
      general: '一般通知',
    },
    priority: {
      low: '低',
      normal: '普通',
      high: '高',
      urgent: '紧急',
    },
    movement: {
      receive: '入库',
      issue: '领用到工单',
      adjust_in: '盘盈',
      adjust_out: '盘亏',
      return: '退回',
    },
    tyreEvent: {
      fit: '安装',
      remove: '拆下',
      retread: '翻新',
      scrap: '报废',
      inspect: '检查',
    },
    kpi: {
      assets: '车队资产',
      available: '{{count}} 可用',
      blocked: '不可用车辆',
      blockedHint: '维修、故障或合规冻结',
      openWorkOrders: '未结维修工单',
      lowStockParts: '{{count}} 个低库存备件',
      monthCost: '本月成本',
      fittedTyres: '{{count}} 条已安装轮胎',
    },
    status: {
      active: '可用',
      workshop: '维修中',
      breakdown: '故障',
      compliance_hold: '合规冻结',
      off_road: '停驶',
      sold: '已售',
      draft: '草稿',
      open: '未结',
      in_progress: '处理中',
      waiting_parts: '待备件',
      vendor_work: '外部维修',
      completed: '已完成',
      closed: '已关闭',
      cancelled: '已取消',
      in_stock: '在库',
      fitted: '已安装',
      repair: '维修',
      retread: '翻新',
      scrapped: '报废',
      expiring: '即将到期',
      hold: '冻结',
      expired: '已过期',
      waived: '豁免',
      triaged: '已分流',
      approved: '已批准',
      waiting_driver: '等待司机',
    },
    panel: {
      recentRequests: '最近服务请求',
      complianceAlerts: '合规提醒',
      driverNotifications: '司机 WhatsApp 记录',
    },
    next: {
      title: '下一步实施切片',
      workshopTitle: '维修工单 MVP',
      workshopText: '创建、计价并关闭车辆维修工单。',
      inventoryTitle: '备件与轮胎',
      inventoryText: '领用备件，并跟踪轮胎安装、换位、维修和报废。',
      dispatchTitle: '调度可用性信号',
      dispatchText: '阻止 TMS/HMS/SVC 分配不可用车辆。',
    },
  },

  // ─── 租户管理 ──────────────────────────────────
  tenantAdmin: {
    title: '租户管理',
    subtitle: '管理租户模块、方案与权限',
    back: '返回',
    edit: '编辑',
    save: '保存',
    cancel: '取消',
    refresh: '刷新',
    core: '核心',
    saveChanges: '保存更改',
    authControl: '权限控制',
    connectorSetup: '连接器设置',
    skillPolicies: '技能策略',
    manageConnectors: '管理连接器',
    managePolicies: '管理策略',
    tenantInfo: '租户信息',
    operationalModules: '运营模块',
    modulesDesc: '控制该租户显式启用的 SDK 模块。这里不会自动勾选模块，也不会写回旧模块代码。',
    tenantMembers: '租户成员',
    loading: '加载中…',
    loadingTenants: '加载租户中…',
    loadingConnectors: '加载连接器中…',
    noMembers: '暂无成员',
    noConnectors: '尚未配置连接器。此区域显示租户的集成点。',
    noTenantsMatch: '没有匹配筛选条件的租户',
    searchPlaceholder: '按名称或标识搜索…',
    allTenants: '所有租户',
    logisticsTenants: '物流租户（无 FMS）',
    freightTenants: '货运租户（含 FM）',
    fields: {
      name: '名称',
      slug: '标识',
      plan: '方案',
      status: '状态',
      currency: '币种',
      locale: '语言区域',
      timezone: '时区',
      country: '国家',
      taxId: '税号',
      companyRegNo: '公司注册号',
      businessDesc: '业务描述',
    },
    placeholders: {
      taxId: '例如 GST 注册号',
      companyRegNo: '例如 UEN 201234567M',
      businessDesc: '简要描述公司业务…',
    },
    moduleCategory: {
      business: '业务管理',
      vertical: '垂直模块',
      integration: '集成与连接器',
    },
    modules: {
      OMS: '订单 (OMS)',
      CRM: '关系管理',
      INBOX: '收件箱 (INBOX)',
      AIC: 'AI 对话 (AIC)',
      OPI: '运营智能 (OPI)',
      AI: 'AI Agentic 控制 (AI)',
      TA: '任务管理 (TA)',
      RM: '关系管理 (RM)',
      TM: '运输英雄 (TM)',
      TP: '运输计划 (TP)',
      CT: '集装箱拖车 (CT)',
      WM: '仓储管理 (WM)',
      CF: 'CFS 仓库 (CF)',
      FM: '货运管理 (FM)',
      SC: '供应链 (SC)',
      MT: '海事与港口代理 (MT)',
      TMS: 'Transport Hero V1 (TMS)',
      HMS: '集装箱拖车 (HMS)',
      FLM: '车队管理 (FLM)',
      DMS: '堆场系统 (DMS)',
      FMS: '货运 (FMS)',
      SAS: '海事工作台 (SAS)',
      WMS: '仓储 (WMS)',
      HRM: '人力资源 (HRM)',
      ACC: '财务 (ACC)',
      SET: '设置 (SET)',
      SOC: '货主箱 (SOC)',
      SCM: '供应链 (SCM)',
      SVC: '服务管理 (SVC)',
      CW: 'CargoWise 连接器',
      CERP: 'CargoERP 连接器',
      EZSH: 'EZShip 连接器',
      SFRT: 'SysFreight 连接器',
      PRO2: 'PRO2 连接器',
      KDCFS: 'KDCFS 连接器',
      QB: 'QuickBooks 连接器',
      XERO: 'Xero 连接器',
      ORCL: 'Oracle ERP 连接器',
      SAP: 'SAP 连接器',
      NETSUITE: 'NetSuite 连接器',
      FR8: 'FR8LAN 连接器',
      FLINK: 'FreightLink 连接器',
      KFLD: 'Keyfield 连接器',
      OPTTRK: 'Opt-E-Truck 连接器',
      OPTHUB: 'Opt-E-Hub 连接器',
    },
    moduleDesc: {
      OMS: '核心业务：订单、报价、管道、客户',
      CRM: '关系智能：销售管道、联系人、客户 360、流失挽回与自助门户信号',
      INBOX: '消息、邮件、WhatsApp、任务、审批',
      AIC: 'AI 协作对话、侧边聊天面板与代理工作区',
      OPI: 'AI 运营智能工作台：绩效分析、事故、服务信号、库存异常、EHS 与管理简报',
      AI: 'SDK 治理的 AI 控制层：工作台、prompt profile、审批、上下文与审计',
      TA: 'SDK 菜单集：任务、行动、排期、附件、分派与报表',
      RM: '关系管理：客户、联系人、供应商、合作伙伴与服务关系层',
      TM: '新一代动态运输配置：收件箱、Job Order、Trips、Billing、Planner、PWA 任务、分享与报表',
      TP: 'AI Agentic 原生运输计划：planner、约束、成本、单证，以及需要审批的 AI planning proposal',
      CT: '新一代动态集装箱拖车配置：Job Order、箱与趟次、司机团队、PWA 任务、分享、计费与报表',
      WM: '新一代动态仓储配置：收货、库存、拣货、上架、小时资源计划、PWA 任务、分享与报表',
      CF: '新一代动态 CFS 仓库配置：集装箱、货物理货、拆柜/装柜、仓租、放货、FM 交接、PWA 任务、分享与计费',
      FM: '新一代动态货运配置：Shipment、单证、报价、成本、账单交接、分享与报表',
      SC: '供应链配置：订单、供应商、采购、履约、库存与单证控制',
      MT: '海事与港口代理配置：船舶、港口挂靠、泊位、理货、单证、服务单与代垫结算',
      TMS: '旧版本地运输计划、调度、计费与司机趟次',
      HMS: 'FCL 集装箱拖车、港口操作、IMP/RET、COL/EXP',
      FLM: '车辆资产、维修工单、备件、轮胎、合规与车队成本',
      DMS: '堆场闸口、场内操作、修箱与检验',
      FMS: '海运/空运进出口、CFS、报关、费率',
      SAS: '船务与港口代理、港口挂靠单证、支线船运营与总代协同',
      WMS: '入库、库存、出库、工单',
      HRM: '员工、考勤、薪资、请假、HR 自助',
      ACC: '应收/应付、日记账、税码、财务报表',
      SET: '公司设置、团队、计费、模板',
      SOC: '货主箱管控 — 始终启用',
      SCM: '销售订单、采购订单、库存、产品目录、履约管理',
      SVC: '服务工单、设备资产管理、施工队排班、现场作业',
      CW: 'WiseTech CargoWise One / ediEnterprise 集成',
      CERP: 'CargoERP 旧系统数据桥接',
      EZSH: 'EZShip 旧系统数据桥接',
      SFRT: 'SysFreight ERP 数据桥接',
      PRO2: 'PRO2 CargoConnect 数据桥接',
      KDCFS: 'KDCFS CFS 系统数据桥接',
      QB: 'Intuit QuickBooks 会计同步',
      XERO: 'Xero 云会计同步',
      ORCL: 'Oracle ERP / NetSuite 集成',
      SAP: 'SAP S/4HANA / Business One 集成',
      NETSUITE: 'Oracle NetSuite ERP 集成',
      FR8: 'FR8LAN 货运市场连接器',
      FLINK: 'FreightLink 订舱追踪连接器',
      KFLD: 'Keyfield 物流系统连接器',
      OPTTRK: 'Opt-E-Truck 车队管理连接器',
      OPTHUB: 'Opt-E-Hub 仓储管理连接器',
    },
    stats: {
      totalJobs: '总工单',
      members: '成员',
      lastActive: '最后活跃',
      connectors: '连接器',
    },
    summary: {
      totalTenants: '租户总数',
      active: '活跃',
      freight: '货运 (FM)',
      totalJobs: '总工单',
    },
    filter: {
      all: '全部',
      logistics: '物流',
      freight: '货运',
    },
    listCols: {
      tenant: '租户',
      status: '状态',
      plan: '方案',
      modules: '模块',
      jobs: '工单',
      lastActivity: '最后活动',
    },
    connectorCols: {
      connector: '连接器',
      type: '类型',
      endpoint: '端点',
      policy: '策略',
      mode: '模式',
      status: '状态',
    },
    memberCols: {
      name: '姓名',
      email: '邮箱',
      role: '角色',
      joined: '加入时间',
      actions: '操作',
    },
    toast: {
      modulesSaved: '模块已保存',
      modulesSaveFailed: '保存模块失败',
      tenantInfoSaved: '租户信息已保存',
      tenantInfoSaveFailed: '保存租户信息失败',
      memberUpdated: '成员已更新',
      memberUpdateFailed: '更新成员失败',
      switchedTenant: '已切换到租户 {{name}}',
      switchFailed: '切换租户上下文失败',
      loadMembersFailed: '加载成员失败',
    },
  },
  chat: {
    searchPlaceholder: '搜索…',
    newChat: '新对话',
    noSessions: '暂无对话\n点击 + 开始',
    noSearchResults: '没有匹配的对话',
    archive: '归档',
    delete: '删除',
    archivedCount: '已归档 ({{count}})',
    restore: '恢复',
    managePrompts: '管理快捷提示',
    promptLabel: '简称（如 📋 今日摘要）',
    promptContent: '要问 AI 的完整问题…',
    autoExecute: '点击后立即发送',
    noPrompts: '暂无快捷提示。点击"添加"创建一个。',
    confirmDelete: '确定要删除「{{label}}」吗？',
    deleteFailed: '删除对话失败',
    archived: '对话已归档',
    restored: '对话已恢复',
    chatTitle: '对话',
    thinking: '思考中…',
    share: '分享对话',
    typeToStart: '在下方输入开始对话',
    startTyping: '说点什么开始吧…',
    quickPrompts: '快捷提示',
    inputPlaceholder: '随便问… (Enter 发送, Shift+Enter 换行)',
    stopRecording: '点击停止',
    voiceInput: '点击说话',
    stop: '停止生成',
    disclaimer: 'AI 回复可能不准确，请始终核实关键信息。',
    shareTitle: '分享对话',
    selectMessages: '选择消息',
    shareExpiry: '链接过期时间',
    generateLink: '生成分享链接',
    copied: '已复制！',
    copy: '复制',
    sharedConversation: '分享的对话',
    shareWhatsApp: '通过 WhatsApp 分享',
    shareInfo: '任何人都可以通过此链接查看对话，无需登录。',
    expiresAt: '过期时间',
  },

  // ── 财务模块 ──
  finance: {
    method: {
      bank_transfer: '银行转账',
      cheque: '支票',
      cash: '现金',
      paynow: 'PayNow',
      fps: 'FPS',
      other: '其他',
    },
    col: {
      document: '单据',
      method: '方式',
      date: '日期',
      amount: '金额',
      unallocated: '未分配',
      allocated: '已分配',
      status: '状态',
    },
    kpi: {
      documents: '单据数',
      unallocatedExposure: '未分配敞口',
    },
    controls: {
      title: '结算控制',
      committedCash: '已确认现金流',
      allocationOpen: '分配仍在进行',
      unallocatedAmount: '未分配金额',
      requiresApproval: '需要审批',
      none: '无',
      yes: '是',
      no: '否',
    },
    action: {
      assist: '辅助',
      refresh: '刷新',
      void: '作废',
      reverse: '冲销',
      apply: '冲抵',
    },
    dock: {
      header: '{{title}} 抬头',
      appliedAllocations: '已分配明细',
      noAllocations: '暂无分配记录。',
      lineBreakdown: '收付款行明细',
      noLines: '暂无行明细。',
      outstandingDocs: '未结对应单据',
      noOutstanding: '未找到该客户/供应商的{{type}}未结单据。',
      outstandingAmt: '未结金额',
      noDueDate: '无到期日',
    },
    toast: {
      allocationApplied: '分配成功',
      allocationReversed: '冲销成功',
      noUnallocated: '无可分配余额',
      loadFailed: '加载{{entity}}失败',
      allocationFailed: '分配失败',
      reverseFailed: '冲销失败',
      voidConfirm: '确认作废此{{kind}}？已有分配将被冲销。',
      voided: '{{title}}已作废',
      voidFailed: '作废{{entity}}失败',
    },
    empty: {
      selectRecord: '选择一条记录以检查分配状态和对应未结单据。',
    },
  },

  hmsMatchbox: {
    title: 'MatchBox 重用线索',
    subtitle: 'HMS 集装箱重用的 WhatsApp 群发现看板。调度员可以复核证据、确认供给或需求，并在清单审批后准备提交 MatchBox API。',
    searchPlaceholder: '搜索船公司、箱型、联系人、地点或消息…',
    shown: '显示 {{count}} 条',
    loadingLeads: '正在加载 MatchBox 重用线索…',
    pendingExtraction: '待提取',
    whatsappGroup: 'WhatsApp 群',
    view: '查看',
    empty: {
      title: '暂无重用线索',
      description: '只读 WhatsApp 监控会在群连接器采集到箱号、船公司、地点或时间信号后填充此看板。',
    },
    badge: {
      readOnlyMonitor: '只读监控',
      readOnlySource: '只读来源',
      sendDisabled: '发送已禁用',
    },
    stats: {
      totalLeads: '线索总数',
      needReview: '待复核',
    },
    status: {
      all: '全部状态',
      new: '新线索',
      needsReview: '待复核',
      matched: '已匹配',
      converted: '已转候选',
      discarded: '已丢弃',
    },
    intent: {
      all: '全部意图',
      supply: '供给',
      demand: '需求',
      question: '问题',
      reply: '回复',
      unknown: '未知',
    },
    fields: {
      status: '状态',
      intent: '意图',
      opportunity: '机会',
      source: '来源',
      sender: '发送人',
      timing: '时间',
      confidence: '置信度',
      group: '群组',
      messageId: '消息 ID',
      messageTime: '消息时间',
      shippingLine: '船公司',
      containerType: '箱型',
      containerNumber: '箱号',
      bookingNumber: '订舱号',
      location: '地点',
      personInCharge: '负责人',
      remark: '备注',
    },
    checklistStatus: {
      not_required: '无需处理',
      pending: '待处理',
      ready: '已就绪',
      warning: '警告',
      blocked: '阻塞',
      approved: '已批准',
      rejected: '已拒绝',
    },
    approvalStatus: {
      not_requested: '未申请',
      pending: '待审批',
      approved: '已批准',
      rejected: '已拒绝',
    },
    checklist: {
      loading: '正在加载清单…',
      convertFirst: '请先把此线索转为候选记录。系统会创建包含时间、负责人、备注和附件引用的就绪清单。',
      reuseGate: '重用门禁',
      reuseGateHint: '所有硬性阻塞项就绪后，才应运行 MatchBox CreateReuse。',
      canCreateReuse: '可创建重用',
      notReady: '尚未就绪',
      runDryRun: '运行 dry-run',
      createReuseDryRun: 'CreateReuse dry-run',
      activityDryRun: '活动 dry-run',
      billingDryRun: '计费 dry-run',
      billingRulePlaceholder: '计费规则集，例如 hms-matchbox-reuse-sg-v1',
      eligibility: '资格检查',
      readyToCallMatchbox: '可调用 MatchBox',
      missingFields: '字段缺失',
      gateClear: '门禁通过',
      blocked: '阻塞',
      activitySync: '活动同步',
      activityRecords: '{{count}} 条活动记录',
      billingDraft: '计费草稿',
      lines: '{{count}} 行',
      reuseCreated: '已创建 MatchBox 重用：',
      requiredBefore: '前置要求',
      source: '来源',
      attachmentRefs: '{{count}} 个附件引用',
      addRef: '添加引用',
      attachment: '附件',
      attachmentPlaceholder: '粘贴文档链接、照片引用或文件名',
      attach: '附加',
    },
    detail: {
      title: '重用线索证据',
      originalMessage: '原始 WhatsApp 消息',
      aiConfidence: 'AI 提取置信度',
      confidenceHint: '低于 65% 应保持在待复核状态，直到调度员检查消息和支持证据。',
      nextWorkflow: '下一步流程',
      nextWorkflowHint: '调度员验证清单后，将线索转为 MatchBox 候选记录。',
      candidateCreated: '候选已创建',
      convertToCandidate: '转为候选',
      readinessChecklist: '就绪清单',
      readinessChecklistHint: '清单项目包含时间、负责人、备注和附件引用。',
    },
    errors: {
      loadLeads: '加载 MatchBox 重用线索失败',
      loadChecklist: '加载候选清单失败',
      convertLead: '线索转候选失败',
      updateChecklist: '更新清单项失败',
      attachEvidence: '添加证据引用失败',
      updateReadiness: '更新候选就绪状态失败',
      runEligibility: '运行资格 dry-run 失败',
      runCreateReuse: '运行 CreateReuse dry-run 失败',
      runActivitySync: '运行活动同步 dry-run 失败',
      runBillingDraft: '运行计费草稿 dry-run 失败',
    },
  },
  hmsPricing: {
    title: 'HMS 价格',
    subtitle: '报价与费率、计费规则、成本方法',
    tabs: {
      rates: '报价 / 费率',
      billing: '计费',
      costing: '成本',
    },
  },
  hmsRates: {
    title: '费率 / 报价',
    description: '这里只放标准费率、客户专属费率、报价和 rate card；不是成本，也不是账单。',
    searchPlaceholder: '报价号、客户、路线…',
    rateCount: '{{count}} 条费率',
    empty: '暂无费率',
    kpi: {
      totalRates: '费率总数',
      active: '生效中',
      expiring: '30 天内到期',
      drafts: '草稿',
    },
    status: {
      all: '全部状态',
      draft: '草稿',
      sent: '已发送',
      accepted: '已接受',
      rejected: '已拒绝',
      expired: '已过期',
    },
    fields: {
      quoteNo: '报价号',
      customer: '客户',
      route: '路线',
      container: '集装箱',
      baseRate: '基础费率',
      surcharges: '附加费',
      total: '合计',
      status: '状态',
      validFrom: '生效日期',
      validTo: '截止日期',
      actions: '操作',
    },
    actions: {
      newRate: '新建费率',
    },
  },
};

export default zh;
